import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { ApiError, apiErrorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatInrMinor } from '../../lib/constants';
import { AppShell } from '../components/AppShell';
import { ExamFeeNotice } from '../components/ExamFeeNotice';
import {
  createPaymentOrder,
  ensureSubjectFeeQuotation,
  pollPaymentOrderStatus,
  type PaymentOrderResult,
} from '../lib/registration-api';
import { openRazorpayCheckout } from '../lib/razorpay-checkout';

const PRODUCT_LABELS: Record<string, string> = {
  SUBJECT_TUITION: 'Subject tuition',
  ASSESSMENT_FEE: 'Assessment fee',
  LEARNING_SUPPORT_FEE: 'Learning support fee',
};

async function loadSubjectQuotation(applicationId: string) {
  return ensureSubjectFeeQuotation(applicationId);
}

export function SubjectFeePaymentPage(): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { applicationId = '' } = useParams();
  const { token, loading } = useAuth();
  const [order, setOrder] = useState<PaymentOrderResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const quotationQuery = useQuery({
    queryKey: ['subject-quotation', applicationId],
    queryFn: () => loadSubjectQuotation(applicationId),
    enabled: Boolean(token && applicationId),
    retry: false,
  });

  const quotationId = quotationQuery.data?.id ?? null;
  const payableMinor = quotationQuery.data?.payableTotalMinor ?? null;

  useEffect(() => {
    if (!token || !quotationId || order) return;
    let cancelled = false;
    setBusy(true);
    createPaymentOrder(quotationId)
      .then((result) => {
        if (!cancelled) setOrder(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? apiErrorMessage(err.problem) : 'Could not start payment');
        }
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, quotationId, order]);

  const examNotice = useMemo(
    () => quotationQuery.data?.snapshot?.notices?.find(
      (notice) => notice.code === 'CAMBRIDGE_EXAM_FEE_NOTE',
    )?.text,
    [quotationQuery.data?.snapshot?.notices],
  );

  const pricingLines = quotationQuery.data?.snapshot?.pricing ?? [];

  if (!loading && !token) return <Navigate to="/login" replace />;
  if (!applicationId) return <Navigate to="/dashboard" replace />;

  if (quotationQuery.isLoading) {
    return (
      <AppShell title="Subject fees">
        <p className="text-muted">Preparing your fee quotation…</p>
      </AppShell>
    );
  }

  if (quotationQuery.isError || !quotationId || payableMinor === null) {
    const message = quotationQuery.error instanceof ApiError
      ? apiErrorMessage(quotationQuery.error.problem)
      : 'Could not load your subject fee quotation.';
    return (
      <AppShell title="Subject fees">
        <div className="error-banner" role="alert">{message}</div>
        <div className="form-actions">
          <Link className="btn btn-secondary" to="/dashboard">Back to dashboard</Link>
        </div>
      </AppShell>
    );
  }

  async function waitForCapture(orderId: string): Promise<void> {
    try {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        const status = await pollPaymentOrderStatus(orderId);
        if (status.status === 'CAPTURED') {
          await queryClient.invalidateQueries({ queryKey: ['my-learners'] });
          await queryClient.invalidateQueries({ queryKey: ['parent-home'] });
          navigate(`/applications/${applicationId}/admission`);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      setError('Payment is still processing. Check your dashboard in a moment.');
    } catch (err) {
      setError(err instanceof ApiError ? apiErrorMessage(err.problem) : 'Could not confirm payment');
    } finally {
      setConfirming(false);
    }
  }

  async function payNow(): Promise<void> {
    if (!order) return;
    setBusy(true);
    setError(null);
    try {
      await openRazorpayCheckout({
        order,
        description: 'Subject fees',
        onSuccess: () => {
          setConfirming(true);
          void waitForCapture(order.paymentOrderId);
        },
        onDismiss: () => setBusy(false),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment could not start');
    } finally {
      setBusy(false);
    }
  }

  const isFree = BigInt(payableMinor) === 0n;

  return (
    <AppShell width="wide" title="Subject fees">
      <div className="page-header">
        <p className="lede">
          This is your final quotation for approved subjects. The amount below is payable now.
        </p>
      </div>

      {error ? <div className="error-banner" role="alert">{error}</div> : null}

      <div className="card card-flush">
        <div className="card-body">
          <table className="summary-table">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Amount</th>
              </tr>
            </thead>
            <tbody>
              {pricingLines.length > 0 ? pricingLines.map((line) => (
                <tr key={`${line.productCode}-${line.grossAmountMinor}`}>
                  <td>{PRODUCT_LABELS[line.productCode] ?? line.productCode.replaceAll('_', ' ').toLowerCase()}</td>
                  <td className="money">{formatInrMinor(line.grossAmountMinor)}</td>
                </tr>
              )) : (
                <tr>
                  <td>Subject fees</td>
                  <td className="money">{formatInrMinor(payableMinor)}</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="summary-total" aria-live="polite">
            <span className="summary-total-label">Amount payable</span>
            <span className={`summary-total-value${isFree ? ' summary-free' : ''}`}>
              {isFree ? 'No fee' : formatInrMinor(payableMinor)}
            </span>
          </div>
        </div>
      </div>

      <div className="section-gap">
        <ExamFeeNotice text={examNotice} />
      </div>

      {confirming ? (
        <div className="notice notice-info section-gap" role="status">
          <strong>Confirming your payment</strong>
          <p>This usually takes a few seconds. Please do not close this window.</p>
        </div>
      ) : (
        <div className="form-actions">
          <Link className="btn btn-ghost" to="/dashboard">Back to dashboard</Link>
          <button
            className="btn btn-lg"
            type="button"
            onClick={payNow}
            disabled={busy || !order}
          >
            {busy ? 'Opening checkout…' : `Pay ${formatInrMinor(payableMinor)}`}
          </button>
        </div>
      )}
    </AppShell>
  );
}
